import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { GlobalLayoutProvider, GlobalLayoutContext } from '$contexts/global-layout.jsx';

import {
    Footer,
    HeaderLanding,
    LandingHero,
    Meta
} from '$partials/global/index.js';

import { getAllModules } from '$utilities/data.js';
import { useLocale } from '$contexts/locale.jsx';
import ModuleDispatcher from '$partials/modules/dispatcher.jsx';
import ModuleRelatedCarousel from '$partials/modules/individual/related-carousel';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const Marker = ({ colour = '#FC5200' }) => {
    return (
        <svg
            className='inline'
            style={{ width: '8px', height: '8px' }}
            viewBox='0 0 8 8'
            xmlns='http://www.w3.org/2000/svg'>
            <circle
                cx='4'
                cy='4'
                fill={colour}
                r='4' />
        </svg>
    );
};

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const LandingPage = ({ data, domain }) => {
    const [stylingWhite, setStylingWhite] = useState(true);

    const { translate: t } = useLocale();

    const page = data?.page;
    const subPage = data?.subPage;

    if (!subPage && !page) { return null; }

    const modules = getAllModules(page || subPage);
    const animatedText = (page?.animatedText || subPage?.animatedText);
    const ctaLabel = (page?.ctaLabel || subPage?.ctaLabel);
    const ctaLink = (page?.ctaLink || subPage?.ctaLink);
    const headerTitle = (page?.headerTitle || subPage?.headerTitle);
    const heroImage = (page?.heroImage || subPage?.heroImage);
    const relatedResources = (page?.relatedResources || subPage?.relatedResources);
    const subheading = (page?.subheading || subPage?.subheading);
    const tickerText = (page?.tickerText || subPage?.tickerText);

    return (
        <>
            <Meta page={(page || subPage)} />

            <main id='main-content'>
                <div className='min-h-screen relative'>
                    <GlobalLayoutProvider>
                        <GlobalLayoutContext.Consumer>
                            {() => (
                                <>
                                    <HeaderLanding
                                        menu={data?.navigation}
                                        setStylingWhite={setStylingWhite}
                                        stylingWhite={stylingWhite} />

                                    <LandingHero
                                        animatedText={animatedText}
                                        ctaLabel={ctaLabel}
                                        ctaLink={ctaLink}
                                        eyebrow={headerTitle}
                                        headerBackgroundImage={heroImage}
                                        heroImageCropLeft={page?.heroImageCropLeft}
                                        subText={subheading}
                                        tickerText={tickerText} />
                                </>
                            )}
                        </GlobalLayoutContext.Consumer>
                    </GlobalLayoutProvider>

                    <section className='relative bg-white z-10 md:-mt-50'>
                        {/* Content */}
                        {modules?.map((module, i) => {
                            return (
                                <ModuleDispatcher
                                    key={i}
                                    module={module}
                                    nextTypename={modules?.[i + 1]?.__typename}
                                    previousTypename={modules?.[i - 1]?.__typename} />
                            );
                        })}

                        {/* Related */}
                        {relatedResources?.items?.length > 0 && (
                            <ModuleRelatedCarousel
                                ctaLabel={t('View All')}
                                ctaLink='/resources'
                                items={relatedResources?.items}
                                title={t('Related Resources')} />
                        )}
                    </section>

                    <div className='pt-30 sm:pt-0 md:pt-20 relative bg-white z-10'>
                        <Footer
                            domain={domain}
                            menu={data?.footer} />
                    </div>
                </div>
            </main>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

Marker.propTypes = {
    colour: PropTypes.string,
};

const propTypes = {
    data: PropTypes.shape({
        footer: PropTypes.shape({
            items: PropTypes.array
        }),
        navigation: PropTypes.object,
        page: PropTypes.object,
        parentPage: PropTypes.object,
        subPage: PropTypes.object
    }),
    domain: PropTypes.string,
};

LandingPage.propTypes = propTypes;

export default LandingPage;
